import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import "./TTSButton.scss";
import { Svg, SVG_ICONS } from "../../Svg/Svg";

import { useSpeechSynthesis } from "react-speech-kit";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../store";
import { LayoutState } from "../../../../store/reducers/layout-reducer";
import { TTS_VOICES_MAP } from "../../../../constants/locale";
import { useTranslation } from "react-i18next";

/**
 * Props for the TTSButton component.
 *
 * @description The TTSButton component is a Text-to-Speech button that reads
 * text content aloud using the Web Speech Synthesis API.
 *
 * @property {string} [value] - The text content to be spoken when the button is clicked.
 * @property {string} [className] - Additional CSS class names to apply to the icon.
 * @property {string} [id] - HTML id attribute for the button element.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any} [onClick] - Click event handler.
 * @property {boolean} [isWhite=false] - Whether to use white icon styling.
 *   When true and not in dark mode, applies white color to the icon.
 *
 * @example
 * // Basic TTS button
 * <TTSButton value="Hello, this text will be spoken" />
 *
 * @example
 * // TTS button with custom styling
 * <TTSButton
 *   value="Click to hear this text"
 *   className="custom-tts"
 *   isWhite={true}
 * />
 *
 * @example
 * // TTS button with click handler
 * <TTSButton
 *   value={titleText}
 *   onClick={handleTTSClick}
 *   testId="tts-button"
 * />
 */
interface Props {
  value?: string;
  className?: string;
  id?: string;
  testId?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any;
  isWhite?: boolean;
}

/**
 * A Text-to-Speech button component using Web Speech Synthesis API.
 *
 * @description TTSButton renders a button that, when clicked, uses the Web Speech
 * Synthesis API to speak the provided text content. It supports multiple languages
 * based on the current application locale and handles various edge cases like
 * page visibility changes and speech synthesis initialization.
 *
 * Features:
 * - Text-to-Speech using Web Speech Synthesis API
 * - Multi-language support based on application locale
 * - Play/Stop toggle functionality
 * - Dark mode support
 * - Automatic speech cancellation on page hide/unload
 * - First-play initialization handling
 * - Error recovery with retry mechanism
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element | null} A button with play/stop icon, or null if TTS is not available
 *
 * @see Props for all available properties
 */
export const TTSButton: React.FC<Props> = (props) => {
  const { value, className = "", id, testId, onClick, isWhite = false } = props;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const { t } = useTranslation();
  const [voice, setVoice] = useState({});
  const [displayIcon, setDisplayIcon] = useState(false);
  const hasInitializedTTS = useRef(false);
  const isPageHidden = useRef(false);
  const shouldRetryOnError = useRef(false);
  const pitch: number = 1;
  const rate: number = 1;

  const { isDarkMode } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { supported } = useSpeechSynthesis();
  const [isVoiceReady, setIsVoiceReady] = useState(false);
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { languageTrans, ttsVoices, isTranslationLoaded } = layoutState;
  const wrapClass = classNames("tts-button", {
    [className]: !!className,
    white: isWhite && !isDarkMode,
  });

  useEffect(() => {
    if (
      isTranslationLoaded &&
      typeof window !== "undefined" &&
      window.speechSynthesis
    ) {
      // stop the speaking when TTS button is playing while changing language
      window.speechSynthesis.cancel();
    }
    if (ttsVoices.length > 0) {
      setDisplayIcon(false);
      const language = languageTrans.split("-")[0];
      var voiceFound = ttsVoices.filter(
        (voiceSynth: any) => voiceSynth.name === TTS_VOICES_MAP[languageTrans]
      )[0];
      if (!voiceFound) {
        voiceFound = ttsVoices.filter(
          (voiceSynth: any) => voiceSynth.lang.substring(0, 2) === language
        )[0];
      }
      if (!voiceFound) {
        setDisplayIcon(false);
      } else {
        setDisplayIcon(true);
        setVoice(voiceFound);
      }
    } else {
      setDisplayIcon(false);
    }
  }, [languageTrans, ttsVoices, isTranslationLoaded]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onClick && onClick(e);
    window.speechSynthesis.cancel();

    if (!isSpeaking && ttsVoices.length > 0) {
      const utterance = new SpeechSynthesisUtterance(value ?? "");
      utterance.voice = voice as SpeechSynthesisVoice;
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.onend = () => {
        setIsSpeaking(false);
        shouldRetryOnError.current = false;
      };

      utterance.onerror = (event) => {
        setIsSpeaking(false);
      };
      utterance.onerror = () => {
        if (shouldRetryOnError.current && !isPageHidden.current) {
          const retryUtterance = new SpeechSynthesisUtterance(value ?? "");
          retryUtterance.voice = utterance.voice;
          retryUtterance.lang = (utterance.voice as SpeechSynthesisVoice).lang;
          retryUtterance.pitch = pitch;
          retryUtterance.rate = rate;
          retryUtterance.volume = 1;
          retryUtterance.onend = () => setIsSpeaking(false);
          retryUtterance.onerror = () => setIsSpeaking(false); // fallback if retry fails
          setIsSpeaking(true);
          window.speechSynthesis.speak(retryUtterance);
        }
        setIsSpeaking(false);
      };

      if (!hasInitializedTTS.current) {
        // mute simulation to address TTS first-play issue
        utterance.volume = 0;
        window.speechSynthesis.speak(utterance);

        setTimeout(() => {
          window.speechSynthesis.cancel();
          hasInitializedTTS.current = true; // mark as initialized
          // Speak the real one
          utterance.volume = 1;
          setIsSpeaking(true);
          shouldRetryOnError.current = true;
          window.speechSynthesis.speak(utterance);
        }, 700);
      } else {
        // Already initialized, speak immediately
        setIsSpeaking(true);
        shouldRetryOnError.current = false;
        window.speechSynthesis.speak(utterance);
      }
    } else {
      shouldRetryOnError.current = false;
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  //Handle speech synthesis cancellation when the page is unloaded or hidden. This ensures that no speech is playing in the background when the user navigates away from the page.
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis)
      return () => {};

    const checkVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        hasInitializedTTS.current = false;
        setIsVoiceReady(true);
      }
    };

    // Try immediately
    checkVoices();

    // Setup voice listener
    const handleVoicesChanged = () => checkVoices();
    window.speechSynthesis.addEventListener(
      "voiceschanged",
      handleVoicesChanged
    );

    // Cancel speech on unload or hidden
    const handleBeforeUnload = () => window.speechSynthesis.cancel();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        isPageHidden.current = true;
        shouldRetryOnError.current = false;
        window.speechSynthesis.cancel();
      } else {
        isPageHidden.current = false;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        handleVoicesChanged
      );
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      isPageHidden.current = true;
      window.speechSynthesis.cancel();
    };
  }, [isTranslationLoaded]);

  if (!displayIcon || !isTranslationLoaded || !isVoiceReady) {
    return null;
  }

  return (
    <button
      onClick={(e) => handleClick(e)}
      id={id}
      className="tts-button-container"
      data-testid={testId}
    >
      {displayIcon &&
        supported &&
        (!isSpeaking ? (
          <Svg
            icon={SVG_ICONS.TTS_PLAY}
            className={wrapClass}
            title={t("PRIVACY_TTS_LISTEN")}
            // onClick={() => handleClick()}
          />
        ) : (
          <Svg
            icon={SVG_ICONS.TTS_STOP}
            className={wrapClass}
            title={t("PRIVACY_TTS_PAUSE")}
            // onClick={() => handleClick()}
          />
        ))}
    </button>
  );
};
